import type { TestConfig, Blueprint, Question } from "@shared/schema";

const STORAGE_KEY = "test_builder_state";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
}

export interface SavedTestState {
  config: TestConfig | null;
  blueprint: Blueprint | null;
  questions: Question[];
  directions: string;
  currentStep: number;
  lastUpdated: string;
  // Optional for backward compatibility with state saved before chat persistence
  chatMessages?: ChatMessage[];
  // The user-entered assessment name. Optional for backward compatibility with
  // state saved before testName was persisted.
  testName?: string;
}

export type SaveResult =
  | { ok: true }
  | { ok: false; reason: 'quota' | 'unknown'; error: unknown };

// Detect QuotaExceededError across browsers. Different browsers throw
// different error names/codes for the same condition.
const isQuotaError = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; code?: number };
  return (
    e.name === 'QuotaExceededError' ||
    e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    e.code === 22 ||
    e.code === 1014
  );
};

export const saveTestState = (state: Omit<SavedTestState, "lastUpdated">): SaveResult => {
  try {
    const dataToSave: SavedTestState = {
      ...state,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    return { ok: true };
  } catch (error) {
    const reason = isQuotaError(error) ? 'quota' : 'unknown';
    console.error(`Failed to save test state (${reason}):`, error);
    return { ok: false, reason, error };
  }
};

export const loadTestState = (): SavedTestState | null => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error("Failed to load test state:", error);
    return null;
  }
};

export const clearTestState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear test state:", error);
  }
};

// The id of the DB row created when the completion (Share) page is first
// reached. Persisted per-tab in sessionStorage so refreshing the completion
// page restores the id instead of saving a duplicate test. Cleared by
// resetTest() when the user starts over.
const SAVED_TEST_ID_KEY = "examflow_saved_test_id";

export const readSavedTestId = (): string | null => {
  try {
    return sessionStorage.getItem(SAVED_TEST_ID_KEY);
  } catch {
    return null;
  }
};

export const writeSavedTestId = (id: string): void => {
  try {
    sessionStorage.setItem(SAVED_TEST_ID_KEY, id);
  } catch (error) {
    console.error("Failed to persist saved test id:", error);
  }
};

export const clearSavedTestId = (): void => {
  try {
    sessionStorage.removeItem(SAVED_TEST_ID_KEY);
  } catch {
    /* ignore */
  }
};

export const hasTestInProgress = (): boolean => {
  const state = loadTestState();
  return state !== null && (state.config !== null || state.blueprint !== null || state.questions.length > 0);
};
